<template>
  <div class="gatewaylist-container" :class="{ 'dashboard-mode': dashboardMode }">
    <div class="table-container">
      <div class="table-header">
        <h2>Gateway List</h2>
        <div class="table-actions">
          <input
            type="text"
            v-model="searchQuery"
            class="search-input"
            placeholder="Search by MAC, device name, location, status"
            @input="handleSearchInput"
          />
        </div>
      </div>
      <table>
        <thead>
          <tr>
            <th @click="sortGateways('gw_mac')">Gateway MAC <span :class="getArrowClass('gw_mac')"></span></th>
            <th @click="sortGateways('tag_count')">Tag Count <span :class="getArrowClass('tag_count')"></span></th>
            <th @click="sortGateways('updatetime')">Update Time <span :class="getArrowClass('updatetime')"></span></th>
            <th @click="sortGateways('devicename')">Device Name <span :class="getArrowClass('devicename')"></span></th>
            <th @click="sortGateways('devicelocation')">Device Location <span :class="getArrowClass('devicelocation')"></span></th>
            <th @click="sortGateways('env_humidity')" title="Gateway temperature/humidity sensor (GXHT30, fw 0.0.0.5+)">Temp / Humidity <span :class="getArrowClass('env_humidity')"></span></th>
            <th @click="sortGateways('active')">Status <span :class="getArrowClass('active')"></span></th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="gateway in filteredGateways" :key="gateway.gw_mac" @click="fetchTagDetails(gateway.gw_mac)">
            <td class="pointer bold-text">{{ gateway.gw_mac }}</td>
            <td>{{ gateway.tag_count }}</td>
            <td>{{ formatSensingTime(gateway.updatetime) }}</td>
            <td>{{ gateway.devicename || 'N/A' }}</td>
            <td>{{ gateway.devicelocation || 'N/A' }}</td>
            <td class="env-cell" :title="envTitle(gateway)">
              <template v-if="hasEnv(gateway)">
                <span class="material-symbols-outlined env-icon" style="font-size: 15px;">device_thermostat</span>{{ Number(gateway.env_temperature).toFixed(1) }} °C
                <span class="env-sep">·</span>
                <span class="material-symbols-outlined env-icon" style="font-size: 15px;">humidity_percentage</span>{{ Number(gateway.env_humidity).toFixed(1) }} %RH
              </template>
              <span v-else class="env-none">—</span>
            </td>
            <td :class="{'connected': gateway.active == 0, 'disconnected': gateway.active != 0}">
              {{ gateway.active == 0 ? 'Connected' : 'Disconnected' }}
            </td>
            <td>
              <button @click.stop="editGateway(gateway)">Edit</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 사이드 패널 -->
    <div class="side-panel" v-if="showSidePanel">
      <h3>Tag List for {{ selectedGatewayMac }}</h3>
      <button @click="closeSidePanel">Close</button>
      <div class="table-container">
        <table>
          <thead>
            <tr>
              <th @click="sortTags('tag_mac')">Tag MAC <span :class="getArrowClass('tag_mac')"></span></th>
              <th @click="sortTags('tag_sensingtime')">Sensing Time <span :class="getArrowClass('tag_sensingtime')"></span></th>
              <th @click="sortTags('tag_rssi')">RSSI <span :class="getArrowClass('tag_rssi')"></span></th>
              <th @click="sortTags('tag_temperature')">Temp <span :class="getArrowClass('tag_temperature')"></span></th>
              <th @click="sortTags('tag_vol')">Battery <span :class="getArrowClass('tag_vol')"></span></th>
              <th @click="sortTags('taglocation')">Last Scanned Zone <span :class="getArrowClass('taglocation')"></span></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="tag in filteredTags" :key="tag.tag_mac">
              <td>{{ tag.tag_mac }}</td>
              <td>{{ formatSensingTime(tag.tag_sensingtime) }}</td>
              <td>
                <span v-if="Math.abs(tag.tag_rssi) <= 70">
                  <span class="material-symbols-outlined rssi-good" style="font-size: 24px;">signal_cellular_alt</span>
                </span>
                <span v-else>
                  <span class="material-symbols-outlined rssi-mid" style="font-size: 24px;">signal_cellular_alt_2_bar</span>
                </span>
              </td>
              <td>
                <span class="material-symbols-outlined thermostat" style="font-size: 16px;">device_thermostat</span>
                <span>{{ tag.tag_temperature }} °C </span>
              </td>
              <td>
                <span :class="getBatteryStatus(tag.tag_vol)">
                  {{ tag.tag_vol >= 2.7 ? 'Good' : tag.tag_vol >= 2.3 ? 'Mid' : 'None' }}
                </span>
                <span class="battery-value"> ({{ tag.tag_vol.toFixed(1) }}v)</span>
              </td>
              <td>{{ tag.taglocation }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- 편집 패널 추가 -->
    <edit-gateway
      v-if="showEditPanel"
      :visible="showEditPanel"
      :gateway="currentGateway"
      @close="showEditPanel = false"
      @save="handleSave"
    />
  </div>
</template>

<script>
import axios from 'axios';
import EditGateway from './EditGatewayList.vue'; // EditGateway 컴포넌트 임포트

export default {
  name: 'GatewayList',
  components: {
    EditGateway, // 컴포넌트 등록
  },
  props: {
    dashboardMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      gateways: [],
      updateInterval: 10,
      updateTimer: null,
      filteredGateways: [],
      searchQuery: '',
      searchTimeout: null,
      tagList: [],
      filteredTags: [],
      selectedGatewayMac: '',
      showSidePanel: false,
      showEditPanel: false, // 편집 패널 상태 추가
      currentGateway: {}, // 현재 수정 중인 게이트웨이 정보 저장
      sortKey: 'gw_mac',
      sortOrder: 1, // 1 for ascending, -1 for descending
      connectedGateways: new Map(), // 연결된 게이트웨이 상태 관리
    };
  },
  mounted() {
    console.log('Fetching data from /api/gatewayList');

    const company = localStorage.getItem('company');
    axios.get('/api/gatewayList', { params: { company } })
      .then(response => {
        console.log('API Response:', response.data);
        this.gateways = response.data;
        this.filterGateways();
        
        // 대시보드 모드에서는 불필요한 API 호출 제거
        // if (!this.dashboardMode) {
        //   this.checkws();
        // }
        
        // 대시보드 모드에서는 자동 갱신을 늦게 시작
        if (this.dashboardMode) {
          setTimeout(() => {
            this.updateInterval = 30;
            this.startUpdateTimer();
          }, 7000); // 7초 후에 자동 갱신 시작 (TagList와 겹치지 않게)
        } else {
          this.startUpdateTimer();
        }
      })
      .catch(error => {
        console.error('Error fetching gateway data:', error);
      });
  },
  methods: {
    startUpdateTimer() {
    console.log(`Starting update timer with interval: ${this.updateInterval} seconds`);
    
    // 기존 타이머가 있다면 제거
    if (this.updateTimer) {
      clearInterval(this.updateTimer);
    }

    // 새로운 타이머 설정
    this.updateTimer = setInterval(() => {
      console.log('Timer triggered, refreshing data...');
      this.fetchGatewayList();
    }, this.updateInterval * 1000);
  },
  async fetchGatewayList() {
    console.log('Fetching gateway list...');
    const company = localStorage.getItem('company');
    try {
      const response = await axios.get('/api/gatewayList', { params: { company } });
      console.log('New gateway data received:', response.data);
      this.gateways = response.data;
      this.filterGateways();
    } catch (error) {
      console.error('Error fetching gateway data:', error);
    }
  },
    fetchTagDetails(gw_mac) {
      this.selectedGatewayMac = gw_mac;
      this.showSidePanel = true;
      // this.checkws();
      console.log(`Fetching tag details for gateway: ${gw_mac}`);
      const company = localStorage.getItem('company');
      axios.get('/api/getTags', { params: { gw_mac: gw_mac, company } })
        .then(response => {
          console.log('Tag details response:', response.data);
          this.tagList = response.data;
          this.filterTags();
        })
        .catch(error => {
          console.error('Error fetching tag data:', error);
        });
    },
    editGateway(gateway) {
      this.currentGateway = { ...gateway }; // 게이트웨이 정보 복사
      this.showEditPanel = true;
    },
    setupWebSocket() {
      const company = localStorage.getItem('company');
      this.ws = new WebSocket(this.$wsUrl, ['web-client']);
      

      this.ws.addEventListener('open', () => {
        this.ws.send(JSON.stringify({
          type: 'init',
          company: company
        }));
        console.log('WebSocket connected');
        this.isConnected = true;
      });

      this.ws.onopen = () => {
        console.log('WebSocket connected');
        this.isConnected = true;
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('Received websocket message:', data);

          if (data.type === 'gateway_ws_list') {
            // 초기 게이트웨이 연결 상태 설정
            data.gateways.forEach(gateway => {
              const gatewayKey = `${gateway.ip}:${gateway.port}`;
              this.connectedGateways.set(gatewayKey, true);
              this.updateGatewayConnectionStatus(gateway.ip);
            });
          } else if (data.type === 'gateway_connected') {
            // 새로운 게이트웨이 연결
            const gatewayKey = `${data.ip}:${data.port}`;
            this.connectedGateways.set(gatewayKey, true);
            this.updateGatewayConnectionStatus(data.ip);
          } else if (data.type === 'gateway_disconnected') {
            // 게이트웨이 연결 해제
            const gatewayKey = `${data.ip}:${data.port}`;
            this.connectedGateways.delete(gatewayKey);
            this.updateGatewayConnectionStatus(data.ip);
          }
        } catch (error) {
          console.error('WebSocket message processing error:', error);
        }
      };
    },
    updateGatewayConnectionStatus(ip) {
      // 게이트웨이 목록에서 해당 IP를 가진 게이트웨이 찾기
      const gateway = this.gateways.find(gw => gw.ip === ip);
      if (gateway) {
        const isConnected = Array.from(this.connectedGateways.keys()).some(key => key.startsWith(ip));
        gateway.connect = isConnected ? '연결됨' : '연결 끊김';
        this.$forceUpdate(); // Vue 업데이트 강제 실행
      }
    },
    // async checkws() {
    //   try {
    //     const company = localStorage.getItem('company');
    //     const response_info = await axios.post('/api/gateway/request-info', { company });
        
    //     if (response_info.status === 429) {
    //       console.warn('Request blocked:', response_info.data.message);
    //       alert(response_info.data.message || '잠시 후 다시 시도해주세요.');
    //       return;
    //     }
    //     console.log('response_info:', response_info.data);

    //     // for (const gateway of this.gateways) {
    //     //   const response = await axios.post('/api/checkactive/ws', {
    //     //     gw_mac: gateway.gw_mac
    //     //   });
    //     //   console.log('response:', response.data);
    //     //   // 연결 상태 업데이트
    //     //   const index = this.gateways.findIndex(g => g.gw_mac === gateway.gw_mac);
    //     //   if (index !== -1) {
    //     //     // Vue의 반응성을 위해 직접 객체 속성 업데이트
    //     //     this.gateways[index].connect = response.data.connect;
    //     //     this.gateways[index].isConnected = Number(response.data.connect) === 0;
        
    //     //     console.log(`Gateway ${gateway.gw_mac} response:`, response.data);
    //     //     console.log(`Gateway ${gateway.gw_mac} updated:`, {
    //     //       connect: this.gateways[index].connect,
    //     //       isConnected: this.gateways[index].isConnected
    //     //     });
           
    //     //   }
    //     // }
    //   } catch (error) {
    //     console.error('Error fetching gateway information:', error);
    //   }
    // },
    async handleSave(updatedGateway) {
      try {
        // API 호출 방식 수정
        const response = await axios.post('/api/gateway/update', {
          gw_mac: updatedGateway.gw_mac,
          devicename: updatedGateway.devicename,
          devicelocation: updatedGateway.devicelocation,
          rssiThreshold: updatedGateway.rssiThreshold
        });

        if (response.data.success) {
          // 성공적으로 업데이트된 경우 로컬 데이터도 업데이트
          const index = this.gateways.findIndex(g => g.gw_mac === updatedGateway.gw_mac);
          if (index !== -1) {
            this.gateways[index] = { ...this.gateways[index], ...updatedGateway };
            this.filterGateways(); // 목록 갱신
          }
          this.showEditPanel = false;
        }
      } catch (error) {
        console.error('Error updating gateway:', error);
      }
    },
    formatSensingTime(sensingTime) {
      if (!sensingTime) return 'N/A';
      const date = new Date(sensingTime);
      const month = date.getMonth() + 1;
      const day = date.getDate();
      const hours = date.getHours();
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const years = date.getFullYear();
      return `${years}/${month}/${day} ${hours}:${minutes}`;
    },
    closeSidePanel() {
      this.showSidePanel = false;
      this.tagList = [];
    },
    getBatteryStatus(voltage) {
      if (voltage >= 2.7) {
        return 'battery_good';
      } else if (voltage >= 2.3 && voltage < 2.7) {
        return 'battery_mid';
      } else {
        return 'battery_none';
      }
    },
    getStatusKeywords(gateway) {
      // online 판정 기준: gw_recent_web.active (0 = 정상 수신, -1 = 끊김)
      let english = '';
      if (gateway.active === 0) {
        english = 'connected';
      } else if (gateway.active === -1) {
        english = 'disconnected';
      } else {
        english = (gateway.active ?? '').toString().toLowerCase();
      }

      // 한글 상태도 함께 대비
      let korean = '';
      if (english === 'connected' || english === '연결됨') {
        korean = '연결됨';
        english = 'connected';
      } else if (english === 'disconnected' || english === '연결 끊김') {
        korean = '연결 끊김';
        english = 'disconnected';
      }

      return [english, korean].filter(Boolean);
    },
    getSortValue(item, key) {
      if (key === 'active') {
        const active = item.active;

        if (typeof active === 'number') {
          return active;
        }

        if (typeof active === 'string') {
          const lower = active.toLowerCase();
          if (lower === 'connected' || lower === '연결됨' || active === '0') {
            return 0;
          }
          if (lower === 'disconnected' || lower === '연결 끊김' || active === '-1') {
            return -1;
          }
        }

        if (active == 0) return 0;
        if (active == -1) return -1;

        // 기본값: 연결 안 됨으로 처리
        return -1;
      }
      // 다른 필드의 경우
      const value = item[key];
      if (value === null || value === undefined) {
        return '';
      }
      return value;
    },
    // 게이트웨이 온습도 센서(0x0C): 정상(status 0) + 10분 이내 측정값만 표시
    hasEnv(gw) {
      if (gw.env_humidity == null || Number(gw.env_status) !== 0) return false;
      if (!gw.env_measured_at) return true;
      return (Date.now() - new Date(gw.env_measured_at).getTime()) < 10 * 60 * 1000;
    },
    envTitle(gw) {
      if (this.hasEnv(gw)) return `Measured ${this.formatSensingTime(gw.env_measured_at)}`;
      if (gw.env_status == null) return 'No temperature/humidity sensor reported (no sensor or firmware < 0.0.0.5)';
      const st = Number(gw.env_status);
      if (st === 1) return 'Gateway has no humidity sensor';
      if (st === 2 || st === 3) return `Sensor error (status ${st})`;
      return 'Reading is stale (older than 10 min)';
    },
    sortGateways(key, keepOrder = false) {
      if (!keepOrder) {
        this.sortOrder = this.sortKey === key ? -this.sortOrder : 1;
      }
      this.sortKey = key;
      
      // 배열 복사 후 정렬 (Vue 반응성 보장)
      const sorted = [...this.filteredGateways].sort((a, b) => {
        let aVal, bVal;
        
        // Status 정렬: gw_recent_web.active 값을 직접 사용
        if (key === 'active') {
          aVal = a.active == 0 ? 0 : a.active == -1 ? -1 : (typeof a.active === 'number' ? a.active : -1);
          bVal = b.active == 0 ? 0 : b.active == -1 ? -1 : (typeof b.active === 'number' ? b.active : -1);
        } else {
          aVal = this.getSortValue(a, key);
          bVal = this.getSortValue(b, key);
        }
        
        // null/undefined 처리
        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;
        
        // 숫자 비교
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          if (aVal < bVal) return -1 * this.sortOrder;
          if (aVal > bVal) return 1 * this.sortOrder;
          return 0;
        }
        
        // 문자열 비교
        const aStr = String(aVal).toLowerCase();
        const bStr = String(bVal).toLowerCase();
        if (aStr < bStr) return -1 * this.sortOrder;
        if (aStr > bStr) return 1 * this.sortOrder;
        return 0;
      });
      
      // 정렬된 배열로 교체
      this.filteredGateways = sorted;
    },
    filterGateways() {
      const query = this.searchQuery.trim().toLowerCase();
      const baseList = [...this.gateways];
      if (!query) {
        this.filteredGateways = baseList;
      } else {
        this.filteredGateways = baseList.filter((gateway) => {
          const mac = (gateway.gw_mac || '').toLowerCase();
          const name = (gateway.devicename || '').toLowerCase();
          const location = (gateway.devicelocation || '').toLowerCase();
          
          // Status 검색: active 값과 표시 텍스트 모두 검색
          const activeValue = gateway.active != null ? gateway.active.toString() : '';
          const statusText = gateway.active == 0 ? 'connected' : gateway.active == -1 ? 'disconnected' : '';
          const statusKeywords = this.getStatusKeywords(gateway).map((word) => word.toLowerCase());

          return (
            mac.includes(query) ||
            name.includes(query) ||
            location.includes(query) ||
            activeValue.includes(query) ||
            statusText.includes(query) ||
            statusKeywords.some((keyword) => keyword.includes(query))
          );
        });
      }
      // 대시보드 모드: 최신 연결순(updatetime) 10개만 표시
      if (this.dashboardMode) {
        this.filteredGateways = [...this.filteredGateways]
          .sort((a, b) => (new Date(b.updatetime) || 0) - (new Date(a.updatetime) || 0))
          .slice(0, 10);
        return;
      }
      this.sortGateways(this.sortKey, true); // 필터링 후 정렬 유지
    },
    sortTags(key, keepOrder = false) {
      if (!keepOrder) {
        this.sortOrder = this.sortKey === key ? -this.sortOrder : 1;
      }
      this.sortKey = key;
      this.filteredTags.sort((a, b) => {
        if (a[key] < b[key]) return -1 * this.sortOrder;
        if (a[key] > b[key]) return 1 * this.sortOrder;
        return 0;
      });
    },
    filterTags() {
      this.filteredTags = this.tagList;
      this.sortTags(this.sortKey, true); // 필터링 후 정렬 유지
    },
    handleSearchInput() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.filterGateways();
      }, 300);
    },
    getArrowClass(key) {
      if (this.sortKey === key) {
        return this.sortOrder === 1 ? 'arrow-up' : 'arrow-down';
      }
      return 'arrow-default';
    },
    beforeUnmount() {
    if (this.updateTimer) {
      clearInterval(this.updateTimer);
    }
    },
    async handleGatewaySave(updatedGateway) {
      try {
        // 게이트웨이 목록 갱신
        const index = this.gateways.findIndex(g => g.gw_mac === updatedGateway.gw_mac);
        if (index !== -1) {
          this.gateways[index] = { ...this.gateways[index], ...updatedGateway };
        }
        // 필요한 경우 게이트웨이 목록 다시 불러오기
        await this.fetchGateways();
      } catch (error) {
        console.error('Error updating gateway:', error);
      }
    }
  },
};
</script>

<style scoped>
.env-cell { white-space: nowrap; }
.env-icon { vertical-align: -3px; color: #1f77c4; margin-right: 1px; }
.env-sep { color: #9ca3af; margin: 0 6px; }
.env-none { color: #9ca3af; }

.gatewaylist-container {
  padding: 10px;
  max-width: 1680px;
  margin: 0 auto;
  background-color: #f5f5f5;
}

/* 테이블 컨테이너 */
.table-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 20px;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px 0 20px;
}

.table-header h2 {
  margin: 0;
}

.table-actions {
  display: flex;
  align-items: center;
}

.search-input {
  width: 260px;
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 13px;
}

.search-input:focus {
  outline: none;
  border-color: #4e73df;
  box-shadow: 0 0 0 2px rgba(78, 115, 223, 0.15);
}

table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

thead th {
  background-color: #f8f9fa;
  padding: 16px 12px;
  text-align: left;
  cursor: pointer;
  position: relative;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #e0e0e0;
  white-space: nowrap;
  user-select: none;
  transition: background-color 0.2s;
}

thead th:hover {
  background-color: #e9ecef;
}

thead th .arrow {
  display: inline-block;
  margin-left: 5px;
}

th .arrow-default::after {
  content: '↕';
  color: #ccc;
  margin-left: 5px;
}

th .arrow-up::after {
  content: '↑';
  color: #4e73df;
  margin-left: 5px;
}

th .arrow-down::after {
  content: '↓';
  color: #4e73df;
  margin-left: 5px;
}

tbody td {
  padding: 12px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: middle;
  color: #333;
}

tbody tr:hover {
  background-color: #f8f9fa;
}

tbody tr:last-child td {
  border-bottom: none;
}

.pointer {
  cursor: pointer;
}

.bold-text {
  font-weight: 600;
  color: #4e73df;
}

/* 버튼 스타일 */
button {
  padding: 6px 12px;
  border: 1px solid #e0e0e0;
  background-color: #4e73df;
  color: white;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s;
}

button:hover {
  background-color: #3a5bc7;
  transform: translateY(-1px);
}

/* 사이드 패널 */
.side-panel {
  position: fixed;
  top: 0;
  right: 0;
  width: 90%;
  max-width: 900px;
  height: 100%;
  background-color: #fff;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.3);
  padding: 20px;
  overflow-y: auto;
  z-index: 1000;
  border-radius: 12px 0 0 12px;
}

.side-panel h3 {
  margin-bottom: 20px;
  color: #333;
  font-size: 20px;
  font-weight: 600;
}

.side-panel button {
  margin-bottom: 20px;
  background-color: #6c757d;
}

.side-panel button:hover {
  background-color: #5a6268;
}

/* 상태 아이콘 및 색상 */
.rssi-good {
  color: #1cc88a;
}

.rssi-mid {
  color: #f6c23e;
}

.rssi-low {
  color: #e74a3b;
}

.battery_good {
  color: #1cc88a !important;
  font-weight: 600;
}

.battery_mid {
  color: #f6c23e !important;
  font-weight: 600;
}

.battery_none {
  color: #36b9cc !important;
  font-weight: 600;
}

.battery-value {
  color: #333;
}

.thermostat {
  color: #e74a3b;
}

.connected {
  color: #1cc88a;
  font-weight: 600;
  background-color: #d4edda;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
}

.disconnected {
  color: #e74a3b;
  font-weight: 600;
  background-color: #f8d7da;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
}

/* 반응형 디자인 - 태블릿 */
@media screen and (max-width: 1200px) {
  .gatewaylist-container {
    padding: 8px;
    max-width: 100%;
  }
  
  table {
    font-size: 13px;
  }
  
  thead th {
    padding: 12px 8px;
  }
  
  tbody td {
    padding: 10px 8px;
  }
  
  .side-panel {
    width: 95%;
    padding: 15px;
  }
}

/* 반응형 디자인 - 모바일 */
@media screen and (max-width: 768px) {
  .gatewaylist-container {
    padding: 5px;
  }
  
  table {
    font-size: 12px;
    min-width: 700px;
  }
  
  thead th {
    padding: 10px 6px;
    font-size: 11px;
  }
  
  tbody td {
    padding: 8px 6px;
  }
  
  .side-panel {
    width: 100%;
    padding: 10px;
    border-radius: 0;
  }
  
  .side-panel h3 {
    font-size: 18px;
  }
  
  /* 모바일에서 일부 컬럼 숨기기 */
  table th:nth-child(2),
  table td:nth-child(2),
  table th:nth-child(4),
  table td:nth-child(4) {
    display: none;
  }
}

/* 반응형 디자인 - 작은 모바일 */
@media screen and (max-width: 480px) {
  .gatewaylist-container.dashboard-mode .table-container {
    max-height: 250px;
  }
  
  .gatewaylist-container.dashboard-mode table {
    font-size: 8px;
  }
  
  .gatewaylist-container.dashboard-mode thead th {
    padding: 2px 1px;
    font-size: 7px;
  }
  
  .gatewaylist-container.dashboard-mode tbody td {
    padding: 2px 1px;
    font-size: 7px;
  }
  
  .gatewaylist-container.dashboard-mode button {
    padding: 1px 3px;
    font-size: 7px;
  }
  
  /* 작은 모바일에서도 Device Name, Device Location, Status만 표시 */
  .gatewaylist-container.dashboard-mode table th:nth-child(1),
  .gatewaylist-container.dashboard-mode table td:nth-child(1),
  .gatewaylist-container.dashboard-mode table th:nth-child(2),
  .gatewaylist-container.dashboard-mode table td:nth-child(2),
  .gatewaylist-container.dashboard-mode table th:nth-child(3),
  .gatewaylist-container.dashboard-mode table td:nth-child(3),
  .gatewaylist-container.dashboard-mode table th:nth-child(7),
  .gatewaylist-container.dashboard-mode table td:nth-child(7) {
    display: none;
  }
}

/* 큰 화면에서의 최적화 */
@media screen and (min-width: 1600px) {
  table {
    font-size: 15px;
  }
  
  thead th {
    padding: 18px 16px;
  }
  
  tbody td {
    padding: 14px 16px;
  }
  
  .side-panel {
    max-width: 1000px;
    padding: 25px;
  }
}

/* Dashboard 컨테이너에서 사용할 때의 스타일 조정 */
.gatewaylist-container.dashboard-mode {
  padding: 0;
  background-color: transparent;
  max-width: none;
  height: 100%;
}

.gatewaylist-container.dashboard-mode .table-container {
  box-shadow: none;
  border: none;
  height: 100%;
  display: flex;
  flex-direction: column;
  margin-bottom: 0;
  overflow: hidden;
}

.gatewaylist-container.dashboard-mode .table-container h2 {
  display: none; /* 대시보드에서는 내부 헤더 완전 숨김 */
}

.gatewaylist-container.dashboard-mode table {
  font-size: 11px;
  flex: 1;
  overflow: auto;
}

.gatewaylist-container.dashboard-mode thead th {
  padding: 6px 4px;
  font-size: 10px;
}

.gatewaylist-container.dashboard-mode tbody td {
  padding: 4px 4px;
  font-size: 10px;
}

.gatewaylist-container.dashboard-mode button {
  padding: 2px 6px;
  font-size: 9px;
}

/* 대시보드 모드에서 모바일 최적화 */
@media screen and (max-width: 768px) {
  .gatewaylist-container.dashboard-mode .table-container {
    overflow-x: auto;
    max-height: 300px;
  }
  
  .gatewaylist-container.dashboard-mode table {
    font-size: 9px;
    min-width: 100%;
    width: 100%;
  }
  
  .gatewaylist-container.dashboard-mode thead th {
    padding: 3px 2px;
    font-size: 8px;
    white-space: nowrap;
  }
  
  .gatewaylist-container.dashboard-mode tbody td {
    padding: 3px 2px;
    font-size: 8px;
    white-space: nowrap;
  }
  
  .gatewaylist-container.dashboard-mode button {
    padding: 2px 4px;
    font-size: 8px;
  }
  
  /* 대시보드 모바일에서 Device Name, Device Location, Status만 표시 */
  .gatewaylist-container.dashboard-mode table th:nth-child(1),
  .gatewaylist-container.dashboard-mode table td:nth-child(1),
  .gatewaylist-container.dashboard-mode table th:nth-child(2),
  .gatewaylist-container.dashboard-mode table td:nth-child(2),
  .gatewaylist-container.dashboard-mode table th:nth-child(3),
  .gatewaylist-container.dashboard-mode table td:nth-child(3),
  .gatewaylist-container.dashboard-mode table th:nth-child(7),
  .gatewaylist-container.dashboard-mode table td:nth-child(7) {
    display: none;
  }
  
  /* 상태 스타일 모바일 최적화 */
  .gatewaylist-container.dashboard-mode .connected,
  .gatewaylist-container.dashboard-mode .disconnected {
    padding: 2px 4px;
    font-size: 7px;
    border-radius: 2px;
  }
}

@media screen and (max-width: 480px) {
  .gatewaylist-container.dashboard-mode .table-container {
    max-height: 250px;
  }
  
  .gatewaylist-container.dashboard-mode table {
    font-size: 8px;
  }
  
  .gatewaylist-container.dashboard-mode thead th {
    padding: 2px 1px;
    font-size: 7px;
  }
  
  .gatewaylist-container.dashboard-mode tbody td {
    padding: 2px 1px;
    font-size: 7px;
  }
  
  .gatewaylist-container.dashboard-mode button {
    padding: 1px 3px;
    font-size: 7px;
  }
  
  /* 작은 모바일에서도 Device Name, Device Location, Status만 표시 */
  .gatewaylist-container.dashboard-mode table th:nth-child(1),
  .gatewaylist-container.dashboard-mode table td:nth-child(1),
  .gatewaylist-container.dashboard-mode table th:nth-child(2),
  .gatewaylist-container.dashboard-mode table td:nth-child(2),
  .gatewaylist-container.dashboard-mode table th:nth-child(3),
  .gatewaylist-container.dashboard-mode table td:nth-child(3),
  .gatewaylist-container.dashboard-mode table th:nth-child(7),
  .gatewaylist-container.dashboard-mode table td:nth-child(7) {
    display: none;
  }
}
</style>
